// src/pages/ClaimSeat.js
//
// Where an invitation link lands: /claim-seat?token=...
//
// The person arriving here may have no account at all, so this handles the
// awkward middle case — showing who the invite is for before asking them to
// sign in or register, then completing the claim once they're authenticated
// with the matching address.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { onAuthStateChanged } from "firebase/auth";
import { auth, functions } from "../firebase/firebaseConfig";
import { isEmailConfirmed } from "../utils/studentLogin";

export default function ClaimSeat() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [invite, setInvite] = useState(null);
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!token) {
      setError("This link is missing its invitation code.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const lookup = httpsCallable(functions, "lookupSeatInvite");
        const { data } = await lookup({ token });
        setInvite(data);
      } catch (err) {
        console.error("Invite lookup error:", err);
        setError(err?.message || "That invitation link isn't valid.");
      }
      setLoading(false);
    })();
  }, [token]);

  const claim = useCallback(async () => {
    setClaiming(true);
    setError("");
    try {
      const claimSeat = httpsCallable(functions, "claimSeat");
      await claimSeat({ token });
      setClaimed(true);
    } catch (err) {
      console.error("Claim error:", err);
      setError(err?.message || "Couldn't claim that seat.");
    }
    setClaiming(false);
  }, [token]);

  if (loading || !authReady) {
    return (
      <div style={s.page}>
        <div style={s.card}><p style={s.body}>Checking your invitation…</p></div>
      </div>
    );
  }

  if (claimed) {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.check}>✓</div>
          <h1 style={s.title}>You're in</h1>
          <p style={s.body}>Your seat is active and your account is unlocked.</p>
          <button
            type="button"
            style={s.primary}
            onClick={() => navigate("/dashboard")}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  const status = invite?.status;
  const unusable = status && status !== "pending";

  return (
    <div style={s.page}>
      <div style={s.card}>
        <h1 style={s.title}>Your SmartBoard Learning seat</h1>

        {error && <div style={s.error}>{error}</div>}

        {unusable && (
          <p style={s.body}>
            {status === "expired"
              ? "This invitation has expired. Ask whoever sent it to invite you again."
              : status === "claimed"
              ? "This invitation has already been used."
              : "This invitation is no longer valid. Ask whoever sent it to invite you again."}
          </p>
        )}

        {invite && !unusable && (
          <>
            <p style={s.body}>
              This seat was reserved for <strong>{invite.email}</strong>
              {invite.state && invite.grade
                ? ` — ${invite.state}, Grade ${invite.grade}.`
                : "."}
            </p>

            {invite.seatType === "student" && (
              <p style={s.minorNote}>
                This is a student seat. If the student is a minor, the account
                should be created and managed by a parent, guardian, or educator.
              </p>
            )}

            {!user ? (
              <>
                <p style={s.body}>
                  Sign in with that address to claim it, or create an account
                  if you don't have one yet.
                </p>
                <Link
                  to={`/register?email=${encodeURIComponent(invite.email)}`}
                  style={s.primaryLink}
                >
                  Create my account
                </Link>
                <Link to="/login" style={s.secondaryLink}>
                  I already have an account
                </Link>
              </>
            ) : !isEmailConfirmed(user) ? (
              <>
                <p style={s.body}>
                  Confirm your email address first, then come back to this link.
                </p>
                <button
                  type="button"
                  style={s.primary}
                  onClick={() => navigate("/verify-email")}
                >
                  Confirm my email
                </button>
              </>
            ) : user.email?.toLowerCase() !== invite.email?.toLowerCase() ? (
              <>
                <p style={s.body}>
                  You're signed in as <strong>{user.email}</strong>, but this
                  invitation is for <strong>{invite.email}</strong>. Sign in
                  with that address to claim it.
                </p>
                <button
                  type="button"
                  style={s.primary}
                  onClick={() => navigate("/login")}
                >
                  Switch account
                </button>
              </>
            ) : (
              <button
                type="button"
                style={{ ...s.primary, opacity: claiming ? 0.6 : 1 }}
                disabled={claiming}
                onClick={claim}
              >
                {claiming ? "Claiming…" : "Claim my seat"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    background: "#fff",
    maxWidth: 460,
    width: "100%",
    borderRadius: 12,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.12)",
    textAlign: "center",
  },
  check: {
    width: 56,
    height: 56,
    lineHeight: "56px",
    borderRadius: "50%",
    margin: "0 auto 16px",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 30,
    fontWeight: 700,
  },
  title: { margin: 0, fontSize: "clamp(1.4rem, 4vw, 1.8rem)", color: "#111" },
  body: { color: "#444", fontSize: 15, lineHeight: 1.6, marginTop: 14 },
  minorNote: {
    fontSize: 12,
    color: "#6b7280",
    lineHeight: 1.5,
    marginTop: 12,
    textAlign: "left",
  },
  primary: {
    width: "100%",
    marginTop: 18,
    padding: "12px 18px",
    borderRadius: 8,
    border: "none",
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },
  primaryLink: {
    display: "block",
    marginTop: 18,
    padding: "12px 18px",
    borderRadius: 8,
    background: "linear-gradient(90deg, #e100ff, #7f00ff)",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
  },
  secondaryLink: {
    display: "block",
    marginTop: 10,
    padding: "12px 18px",
    borderRadius: 8,
    background: "#e5e7eb",
    color: "#111",
    fontSize: 15,
    fontWeight: 600,
    textDecoration: "none",
  },
  error: {
    background: "#fee2e2",
    border: "1px solid #fca5a5",
    color: "#991b1b",
    padding: "10px 12px",
    borderRadius: 8,
    fontSize: 14,
    marginTop: 14,
    textAlign: "left",
  },
};
